if (!customElements.get('swiper-slider')) {
  customElements.define(
    'swiper-slider',
    class SwiperSlider extends HTMLElement {
      constructor() {
        super();
        this.swiper = null;
        this.observer = null;
      }

      connectedCallback() {
        this.swiperContainer = this.querySelector('.swiper');
        this.jsonScript = this.querySelector('script[data-swiper-config]');

        if (!this.swiperContainer || !this.jsonScript) {
          console.warn('SwiperSlider: Missing container or config');
          return;
        }

        this.initObserver();
      }

      initObserver() {
        if (!window.Swiper) {
          window.addEventListener('load', () => this.initObserver(), { once: true });
          return;
        }

        if (!('IntersectionObserver' in window)) {
          this.initializeSwiper();
          return;
        }

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;

              this.observer.disconnect();
              this.initializeSwiper();
            });
          },
          {
            rootMargin: '600px',
          },
        );

        this.observer.observe(this);
      }

      getSwiperConfig() {
        try {
          const userConfig = JSON.parse(this.jsonScript.textContent);

          const defaults = {
            spaceBetween: 20,
            watchOverflow: true,
            a11y: {
              enabled: true,
              prevSlideMessage: 'Previous slide',
              nextSlideMessage: 'Next slide',
            },
          };

          return { ...defaults, ...userConfig };
        } catch (e) {
          console.error('SwiperSlider: JSON error', e);
          return {};
        }
      }

      initializeSwiper() {
        if (this.swiper || !window.Swiper) return;

        const config = this.getSwiperConfig();

        this.classList.add('is-initialized');
        this.swiper = new Swiper(this.swiperContainer, config);

        this.normalizeListSlideRoles();
        this.toggleNavigation();
        this.bindEvents();
      }

      normalizeListSlideRoles() {
        const listWrapper = this.swiperContainer?.querySelector('.swiper-wrapper[role="list"]');

        if (!listWrapper) return;

        listWrapper.querySelectorAll('.swiper-slide').forEach((slide) => {
          slide.setAttribute('role', 'listitem');
        });
      }

      toggleNavigation() {
        if (!this.swiper) return;

        const slidesCount = this.swiper?.slides?.length ?? 0;

        let perView = this.swiper.params?.slidesPerView || 1;

        if (perView === 'auto' && this.swiper.slides?.[0]) {
          const slideWidth = this.swiper.slides[0].offsetWidth || 1;
          perView = Math.floor(this.swiper.width / slideWidth);
        }

        const shouldHide = slidesCount <= perView;

        const nextEl = this.swiper.navigation?.nextEl;
        const prevEl = this.swiper.navigation?.prevEl;

        if (nextEl) nextEl.style.display = shouldHide ? 'none' : '';
        if (prevEl) prevEl.style.display = shouldHide ? 'none' : '';
      }

      bindEvents() {
        if (!this.swiper) return;

        this.swiper.on('resize', () => {
          this.toggleNavigation();
        });

        this.swiper.on('update', () => {
          this.normalizeListSlideRoles();
          this.toggleNavigation();
        });

        this.swiper.on('slideChange transitionEnd', () => {
          this.normalizeListSlideRoles();
        });
      }

      disconnectedCallback() {
        if (this.observer) {
          this.observer.disconnect();
        }

        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }

        this.classList.remove('is-initialized');
      }
    },
  );
}
